import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../../../contexts/AuthContext'
import { supabase } from '../../../shared/lib/supabase'
import MFAVerify from '../components/MFAVerify'

function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [showMFA, setShowMFA] = useState(false)
  const [mfaFactorId, setMfaFactorId] = useState(null)
  const navigate = useNavigate()
  const { signIn, user, loading: authLoading } = useAuth()

  // If user is already authenticated (e.g., from invite link), redirect to app
  useEffect(() => {
    if (!authLoading && user) {
      navigate('/', { replace: true })
    }
  }, [authLoading, user, navigate])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const { error } = await signIn({ email, password })

      if (error) {
        // Check if this is an invited user who never completed onboarding
        if (error.status === 400 || error.message?.includes('Invalid login credentials')) {
          try {
            const res = await fetch('/api/check-invite-status', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email: email.trim().toLowerCase() })
            })
            if (res.ok) {
              const data = await res.json()
              if (data.invite_pending) {
                setError('__invite_pending__')
                setLoading(false)
                return
              }
            }
          } catch {
            // Check failed — fall through to generic error
          }
        }
        setError(error.message)
        setLoading(false)
        return
      }

      // Check if MFA is required
      const { data: aalData, error: aalError } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()

      if (aalError) {
        console.error('Error checking AAL:', aalError)
        navigate('/')
        return
      }

      // If current AAL is less than next AAL, MFA verification is needed
      if (aalData.currentLevel === 'aal1' && aalData.nextLevel === 'aal2') {
        // Get the MFA factor
        const { data: factorsData } = await supabase.auth.mfa.listFactors()
        const totpFactor = factorsData?.totp?.find(f => f.status === 'verified')

        if (totpFactor) {
          setMfaFactorId(totpFactor.id)
          setShowMFA(true)
          setLoading(false)
          return
        }
      }

      // No MFA required or already at aal2
      // Store MFA verification time
      localStorage.setItem('ssa-mfa-verified-at', Date.now().toString())
      navigate('/')
    } catch (err) {
      setError('An unexpected error occurred')
      setLoading(false)
    }
  }

  const handleMFAVerified = () => {
    setShowMFA(false)
    navigate('/')
  }

  const handleMFACancel = async () => {
    setShowMFA(false)
    setMfaFactorId(null)
    // Sign out since they cancelled MFA
    await supabase.auth.signOut()
    setError('MFA verification is required to sign in.')
  }

  if (showMFA) {
    return (
      <MFAVerify
        factorId={mfaFactorId}
        onVerified={handleMFAVerified}
        onCancel={handleMFACancel}
      />
    )
  }

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: '#012438' }}>
      {/* Left panel - branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{ backgroundColor: '#FAFAFF' }}>
        {/* Decorative background shapes */}
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full" style={{ backgroundColor: '#8AD0D6', opacity: 0.08 }} />
        <div className="absolute -bottom-40 -left-40 rounded-full" style={{ width: '500px', height: '500px', backgroundColor: '#012438', opacity: 0.04 }} />
        <div className="absolute top-1/3 right-8 w-72 h-72 rounded-full" style={{ backgroundColor: '#BCC5CD', opacity: 0.12 }} />
        <div className="absolute top-16 left-1/3 w-48 h-48 rounded-full" style={{ backgroundColor: '#8AD0D6', opacity: 0.05 }} />

        {/* Right edge accent line */}
        <div className="absolute right-0 top-0 bottom-0 w-px" style={{ backgroundColor: '#BCC5CD' }} />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-16">
          <img src="/logo.png" alt="SSA Logo" className="w-16 h-16 rounded-2xl object-contain mb-8" />
          <h1 className="text-4xl font-bold leading-tight mb-3" style={{ color: '#012438' }}>
            Sidewalk Security Advisors
          </h1>
          <div className="w-12 h-1 rounded-full mb-5" style={{ backgroundColor: '#8AD0D6' }} />
          <p className="text-2xl font-bold tracking-wide" style={{ color: '#012438' }}>
            Vantage
          </p>
          <p className="text-xs font-medium uppercase tracking-widest mt-1.5" style={{ color: '#7E8F9C' }}>
            Executive Advisory Platform
          </p>
        </div>

        {/* Bottom tagline */}
        <div className="absolute bottom-8 left-16">
          <p className="text-xs tracking-widest uppercase font-medium" style={{ color: '#7E8F9C' }}>
            Secure &middot; Strategic &middot; Advisory
          </p>
        </div>
      </div>

      {/* Right panel - login form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 sm:px-12" style={{ backgroundColor: '#012438' }}>
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8">
            <img src="/logo.png" alt="SSA Logo" className="w-12 h-12 rounded-xl object-contain mx-auto mb-4" />
            <h1 className="text-xl font-bold text-white">Sidewalk Security Advisors</h1>
            <p className="text-sm mt-1" style={{ color: '#7E8F9C' }}>Executive Advisory Platform</p>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-bold text-white">
              Sign in to your account
            </h2>
            <p className="mt-2 text-sm" style={{ color: '#7E8F9C' }}>
              Enter your credentials to access the platform
            </p>
          </div>

          <form className="space-y-5" onSubmit={handleSubmit}>
            {error === '__invite_pending__' ? (
              <div className="border px-4 py-4 rounded-lg text-sm" style={{ backgroundColor: 'rgba(138, 208, 214, 0.1)', borderColor: 'rgba(138, 208, 214, 0.3)' }}>
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#8AD0D6' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                  </svg>
                  <div>
                    <p className="font-medium" style={{ color: '#8AD0D6' }}>Account setup required</p>
                    <p className="mt-1" style={{ color: '#7E8F9C' }}>
                      Your account was created via invitation and requires initial setup. Please check your email for the invitation link to set your password. If you can't find it, contact your administrator to resend the invitation.
                    </p>
                  </div>
                </div>
              </div>
            ) : error && (
              <div className="flex items-start gap-3 border px-4 py-3 rounded-lg text-sm" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', borderColor: 'rgba(239, 68, 68, 0.3)', color: '#fca5a5' }}>
                <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{error}</span>
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-1.5" style={{ color: '#BCC5CD' }}>
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="block w-full px-3.5 py-2.5 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-offset-0 transition-colors"
                style={{ backgroundColor: '#213C4E', borderColor: '#3F5A6A', border: '1px solid #3F5A6A' }}
                placeholder="you@example.com"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label htmlFor="password" className="block text-sm font-medium" style={{ color: '#BCC5CD' }}>
                  Password
                </label>
                <Link
                  to="/forgot-password"
                  className="text-sm font-medium hover:underline"
                  style={{ color: '#8AD0D6' }}
                >
                  Forgot password?
                </Link>
              </div>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="block w-full px-3.5 py-2.5 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-offset-0 transition-colors"
                style={{ backgroundColor: '#213C4E', borderColor: '#3F5A6A', border: '1px solid #3F5A6A' }}
                placeholder="Enter your password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center py-2.5 px-4 border border-transparent rounded-lg text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
              style={{ backgroundColor: '#8AD0D6', color: '#012438' }}
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" style={{ color: '#012438' }}>
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Signing in...
                </>
              ) : 'Sign in'}
            </button>
          </form>

          <p className="mt-8 text-center text-xs" style={{ color: '#536C7C' }}>
            Sidewalk Security Advisors &bull; Executive Advisory Platform
          </p>
        </div>
      </div>
    </div>
  )
}

export default Login
